import { create } from "zustand";
import { useGamificationToastStore } from "./gamification-toast";

interface ClaimRewardResult {
    new_xp: number;
    new_level: number;
    level_up: boolean;
    current_streak: number;
    longest_streak: number;
    streak_updated: boolean;
}

/** Tabs the streak dialog can open on. Mirrors `StreakDetailModal`'s tabs. */
export type StreakModalTab = "calendar" | "challenges" | "milestones" | "freezes";

interface GamificationUIState {
    streakModalOpen: boolean;
    /**
     * Tab the dialog should land on the next time it opens. Set by callers
     * that open it "at" something — the mobile challenges button opens the
     * dialog straight on the challenges list rather than the calendar.
     */
    streakModalTab: StreakModalTab | null;
    claimingChallengeId: string | null;
    openStreakModal: (tab?: StreakModalTab) => void;
    closeStreakModal: () => void;
    setStreakModalOpen: (open: boolean) => void;
    setStreakModalTab: (tab: StreakModalTab | null) => void;
    setClaimingChallengeId: (id: string | null) => void;
    handleClaimReward: (
        challengeId: string,
        deps: {
            challenges: Array<{ id: string; xp_reward: number }>;
            claimReward: (id: string) => Promise<ClaimRewardResult | null>;
        },
    ) => Promise<void>;
}

export const useGamificationUIStore = create<GamificationUIState>()((set) => ({
    streakModalOpen: false,
    streakModalTab: null,
    claimingChallengeId: null,

    openStreakModal: (tab) => set({ streakModalOpen: true, streakModalTab: tab ?? null }),
    closeStreakModal: () => set({ streakModalOpen: false }),
    setStreakModalOpen: (open: boolean) => set({ streakModalOpen: open }),
    setStreakModalTab: (tab) => set({ streakModalTab: tab }),
    setClaimingChallengeId: (id: string | null) => set({ claimingChallengeId: id }),

    handleClaimReward: async (challengeId, deps) => {
        set({ claimingChallengeId: challengeId });
        try {
            const challenge = deps.challenges.find((c) => c.id === challengeId);
            const xpReward = challenge?.xp_reward ?? 0;
            const result = await deps.claimReward(challengeId);
            if (result) {
                const { showXPGain } = useGamificationToastStore.getState();
                showXPGain({
                    xpAmount: xpReward,
                    newXp: result.new_xp,
                    newLevel: result.new_level,
                    levelUp: result.level_up,
                    currentStreak: result.current_streak,
                    longestStreak: result.longest_streak,
                    streakUpdated: result.streak_updated,
                });
            }
        } catch (error) {
            console.error("Failed to claim reward:", error);
        } finally {
            set({ claimingChallengeId: null });
        }
    },
}));
